import { useEffect, useState } from "react";
import { Plus, ServerCog, ShieldCheck } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Btn } from "../../components/Btn";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { MeshDiagram } from "../../components/MeshDiagram";
import { NodeModal } from "../../components/dashboardModals";
import { CacheBar } from "../../components/dashboardModals";
import { type LocalServer } from "../../data";
import { createLocal, listLocals, localsSummary } from "../../api/endpoints";
import { toLocalServer } from "../../api/adapters";
import { useApiFallback } from "../../hooks/useApiFallback";
import { ApiError, type LocalCreate } from "../../api/types";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "offline", label: "Офлайн" },
  { value: "online",  label: "Онлайн" },
  { value: "stale",   label: "Устарел" },
];

export default function LocalsPage() {
  const qc = useQueryClient();
  const push = useToast();
  const [nodeModal, setNodeModal] = useState<LocalServer | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const localsQ = useQuery({ queryKey: ["locals"], queryFn: listLocals });
  const summaryQ = useQuery({ queryKey: ["locals", "summary"], queryFn: localsSummary });

  const createMut = useMutation({
    mutationFn: createLocal,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["locals"] });
      push({ kind: "success", text: "Локальный сервер добавлен" });
      setCreateOpen(false);
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.message || "Не удалось добавить") : "Не удалось добавить";
      push({ kind: "warn", text: msg });
    },
  });

  // Fallback на пустой массив (не mock) — иначе на reload видны фейковые узлы.
  const locals = useApiFallback<LocalServer[]>(
    localsQ.data?.map(toLocalServer),
    localsQ.isError,
    [],
  );
  const localServersData = locals.value;

  const online = summaryQ.data?.online ?? localServersData.filter((s) => s.status === "online").length;
  const stale = summaryQ.data?.stale ?? localServersData.filter((s) => s.status === "stale").length;
  const offline = summaryQ.data?.offline ?? localServersData.filter((s) => s.status === "offline").length;
  const total = summaryQ.data?.total ?? localServersData.length;

  return (
    <>
      <header className="flex flex-col gap-3 pb-5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-[20px] font-semibold leading-tight tracking-[-0.01em] text-[var(--color-fg)] sm:text-[22px]">
            Локальные серверы
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12.5px] text-[var(--color-fg-muted)]">
            <span>{total} узлов</span>
            <Dot />
            <Pill color="success">{online} онлайн</Pill>
            {stale > 0 && (<><Dot /><Pill color="warning">{stale} устарело</Pill></>)}
            {offline > 0 && (<><Dot /><Pill color="danger">{offline} офлайн</Pill></>)}
          </div>
        </div>
        <Btn icon={Plus} variant="primary" onClick={() => setCreateOpen(true)}>
          Зарегистрировать сервер
        </Btn>
      </header>

      {/* Меш — крупная диаграмма */}
      <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
              Меш локальных серверов
            </h2>
            <p className="mt-0.5 text-[11.5px] text-[var(--color-fg-muted)]">
              Кликните по узлу для подробностей. Пунктир — каналы обмена в меше.
            </p>
          </div>
          <ul className="flex items-center gap-3 text-[11.5px] text-[var(--color-fg-muted)]">
            {[
              { c: "var(--color-success)", l: "онлайн" },
              { c: "var(--color-warning)", l: "устарел" },
              { c: "var(--color-danger)", l: "офлайн" },
            ].map((it) => (
              <li key={it.l} className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: it.c }} />
                {it.l}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-4">
          <MeshDiagram onNodeClick={setNodeModal} height={360} locals={localServersData} />
        </div>
      </section>

      {/* Таблица узлов */}
      <section className="mt-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
        <h2 className="text-[14px] font-semibold tracking-[-0.005em] text-[var(--color-fg)]">
          Узлы
        </h2>
        <div className="-mx-1 mt-3 overflow-x-auto">
          <table className="w-full min-w-[720px] text-[12.5px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
                <Th>ID</Th>
                <Th>Локация</Th>
                <Th>Клиенты</Th>
                <Th>Кэш</Th>
                <Th>Посл. синк</Th>
                <Th>Сертификат</Th>
              </tr>
            </thead>
            <tbody>
              {localServersData.map((s, i) => (
                <tr
                  key={s.id}
                  onClick={() => setNodeModal(s)}
                  style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
                  className="stagger-up cursor-pointer border-t border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface-2)]"
                >
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[12px] font-medium">{s.id}</td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <span
                      className="mr-2 inline-block h-2 w-2 rounded-full align-middle"
                      style={{
                        background:
                          s.status === "online" ? "var(--color-success)" :
                          s.status === "stale"  ? "var(--color-warning)" :
                                                  "var(--color-danger)",
                      }}
                    />
                    {s.location}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <ClientsBar clients={s.clients} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <CacheBar gb={s.cacheGB} cap={s.cacheCapGB} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono">
                    {s.status === "offline" ? (
                      <span className="text-[var(--color-danger)]">{s.lastSyncMin} мин</span>
                    ) : s.lastSyncMin > 30 ? (
                      <span className="text-[var(--color-warning)]">{s.lastSyncMin} мин</span>
                    ) : (
                      <span>{s.lastSyncMin} мин</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-success-bg)] px-2 py-0.5 text-[10.5px] font-medium text-[var(--color-success)]">
                      <ShieldCheck size={11} />
                      Активен
                    </span>
                    <span className="ml-2 font-mono text-[11px] text-[var(--color-fg-muted)]">{s.certExpires}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <NodeModal
        modal={nodeModal ? { kind: "node", node: nodeModal } : null}
        onClose={() => setNodeModal(null)}
      />

      <CreateLocalModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(payload) => createMut.mutate(payload)}
        pending={createMut.isPending}
      />
    </>
  );
}

function CreateLocalModal({
  open,
  onClose,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: LocalCreate) => void;
  pending: boolean;
}) {
  const [id, setId] = useState("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState<string>("offline");

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- сброс формы при открытии модалки
      setId("");
      setLocation("");
      setAddress("");
      setStatus("offline");
    }
  }, [open]);

  // Валидация: id — латиница/цифры/дефис/точка 2+; location и address — непустые.
  const idValid = /^[a-zA-Z0-9._-]{2,}$/.test(id.trim());
  const locValid = location.trim().length >= 2;
  const addrValid = address.trim().length >= 2;
  const canSubmit = idValid && locValid && addrValid && !pending;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      id: id.trim(),
      location: location.trim(),
      address: address.trim(),
      status,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <ServerCog size={16} className="text-[var(--color-brand)]" />
          Зарегистрировать локальный сервер
        </span>
      }
      subtitle="Узел появится в меше; сертификат и кэш заполнятся после первого синка"
      size="sm"
      footer={
        <>
          <Btn onClick={onClose}>Отмена</Btn>
          <Btn variant="primary" onClick={submit}>
            {pending ? "Создание…" : "Создать"}
          </Btn>
        </>
      }
    >
      <div className="space-y-3 text-[12.5px]">
        <Field label="ID" hint="латиница, цифры, дефис">
          <input
            value={id}
            onChange={(e) => setId(e.target.value.replace(/\s+/g, ""))}
            placeholder="msk-01"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Локация">
          <input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Москва · ЦО"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Адрес" hint="IP/host:port">
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value.trim())}
            placeholder="10.42.0.11:5390"
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 font-mono text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          />
        </Field>
        <Field label="Статус">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1.5 text-[12.5px] text-[var(--color-fg)] outline-none focus:border-[var(--color-fg-muted)]"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-[var(--color-fg-muted)]">
          {label}
        </span>
        {hint && <span className="text-[10.5px] text-[var(--color-fg-faint)]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function ClientsBar({ clients }: { clients: number }) {
  const max = 320; // условный максимум для шкалы
  const pct = Math.min(100, Math.round((clients / max) * 100));
  return (
    <div className="flex items-center gap-2">
      <span className="w-8 text-right font-mono">{clients}</span>
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[var(--color-surface-3)]">
        <div className="h-full rounded-full bg-[var(--color-brand-2)]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Pill({ color, children }: { color: "success" | "warning" | "danger"; children: React.ReactNode }) {
  const map = {
    success: { bg: "var(--color-success-bg)", fg: "var(--color-success)" },
    warning: { bg: "var(--color-warning-bg)", fg: "var(--color-warning)" },
    danger:  { bg: "var(--color-danger-bg)",  fg: "var(--color-danger)"  },
  };
  return (
    <span className="rounded-full px-1.5 py-0.5 text-[10.5px] font-medium" style={{ background: map[color].bg, color: map[color].fg }}>
      {children}
    </span>
  );
}

function Dot() { return <span className="text-[var(--color-fg-faint)]">·</span>; }

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 font-medium">{children}</th>;
}
